/**
 * App shell for the authenticated surfaces: a collapsible left sidebar (brand
 * block, grouped navigation, presenter-voting + profile + sign-out footer)
 * and a two-line top bar. Pages land in the <Outlet />. Ported from the
 * sister supply-chain app's shell, adapted to this repo's routes/copy — no
 * notification bell (this app has no activity-stream concept) and no
 * "ephemeral storage" badge (this app uses persistent Postgres).
 */

import {
  Activity,
  AlertTriangle,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  LogOut,
  type LucideIcon,
  Menu,
  TrendingUp,
  Truck,
  User,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";

import { DemoLegend } from "@/components/comms";
import { JJWordmark } from "@/components/jj-wordmark";
import { WelcomeDialog, WelcomeDialogTrigger } from "@/components/welcome-dialog";
import { PresenterVotingDialog } from "@/features/voting/presenter-voting-dialog";
import { useHealth, useLogout, useMe } from "@/lib/api";
import { cn } from "@/lib/utils";

type NavItem = {
  href: string;
  label: string;
  icon: LucideIcon;
};

type NavSection = {
  label: string;
  /** Whole section is hidden unless the current user is an admin. */
  adminOnly?: boolean;
  items: NavItem[];
};

const NAV: NavSection[] = [
  {
    label: "Workspace",
    items: [
      { href: "/ordering", label: "Ordering", icon: Truck },
      { href: "/share", label: "Order share", icon: TrendingUp },
      { href: "/guide", label: "Guide", icon: BookOpen },
    ],
  },
  {
    label: "Admin",
    adminOnly: true,
    items: [
      { href: "/admin/users", label: "Users", icon: Users },
      { href: "/admin/activity", label: "Activity", icon: Activity },
    ],
  },
];

// Persist the collapsed rail across reloads so the chrome stays put between
// sessions.
const COLLAPSE_KEY = "pca.sidebar.collapsed";

// localStorage access can throw (storage disabled, or a sandboxed iframe
// without allow-same-origin), so both read and write are guarded — a failure
// degrades to the expanded default rather than throwing out of the shell
// that wraps every authenticated route.
function readCollapsed(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  } catch {
    // Preference just won't persist across reloads — not worth surfacing.
  }
}

export function AppShell() {
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: me } = useMe();
  const { data: health } = useHealth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapsed = () =>
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(next);
      return next;
    });

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  const isAdmin = me?.role === "admin";
  // Amber pill (top bar) whenever the backend isn't running the live model.
  const showLlmPill = health?.llm_mode !== undefined && health.llm_mode !== "live";

  return (
    <div className="bg-background flex h-screen overflow-hidden">
      {/* Sidebar — desktop */}
      <aside
        className={cn(
          "hidden h-screen shrink-0 flex-col border-r transition-[width,background-color,color] duration-200 ease-out lg:flex",
          collapsed
            ? "bg-primary text-primary-foreground border-primary w-16"
            : "bg-card border-border w-64",
        )}
      >
        <SidebarContent
          pathname={pathname}
          isAdmin={isAdmin}
          me={me}
          onLogout={handleLogout}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      </aside>

      {/* Sidebar — mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="border-border bg-card absolute inset-y-0 left-0 flex w-72 flex-col border-r shadow-xl">
            <SidebarContent
              pathname={pathname}
              isAdmin={isAdmin}
              me={me}
              onLogout={handleLogout}
              onNavigate={() => setMobileOpen(false)}
              collapsed={false}
            />
          </aside>
        </div>
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="border-border bg-card flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              className="text-muted hover:bg-accent-light hover:text-foreground inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
                Proactive Comms
              </p>
              <h1 className="font-heading text-foreground truncate text-base sm:text-lg">
                {currentTitle(pathname)}
              </h1>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {showLlmPill && (
              <span
                className="bg-warning-bg border-warning-border text-warning-foreground inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium"
                title="Live generation is off — the backend is running the deterministic test model."
              >
                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                Deterministic test AI
              </span>
            )}
            <WelcomeDialogTrigger />
          </div>
        </header>

        {/* Renders nothing on its own — pops the welcome panel on first visit
            (or when the URL carries `?welcome=1`). */}
        <WelcomeDialog />

        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-5xl px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

interface SidebarUser {
  first_name?: string | null;
  last_name?: string | null;
  email: string;
}

function SidebarContent({
  pathname,
  isAdmin,
  me,
  onLogout,
  onNavigate,
  collapsed,
  onToggleCollapsed,
}: {
  pathname: string;
  isAdmin: boolean;
  me: SidebarUser | undefined;
  onLogout: () => void;
  onNavigate?: () => void;
  collapsed: boolean;
  onToggleCollapsed?: () => void;
}) {
  const displayName = me ? [me.first_name, me.last_name].filter(Boolean).join(" ") : "";

  return (
    <>
      {/* Brand block */}
      <div
        className={cn(
          "flex border-b",
          collapsed
            ? "flex-col items-center gap-1 border-white/15 px-2 py-3"
            : "border-border items-start justify-between gap-2 px-5 py-5",
        )}
      >
        {collapsed ? (
          <>
            <Link
              to="/home"
              onClick={onNavigate}
              aria-label="Proactive Communications Agent — home"
              className="font-heading text-primary-foreground block py-1 text-base font-bold tracking-tight hover:opacity-80"
            >
              J&amp;J
            </Link>
            {onToggleCollapsed && (
              <button
                onClick={onToggleCollapsed}
                aria-label="Expand sidebar"
                className="text-primary-foreground flex h-7 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </>
        ) : (
          <>
            {/* Wordmark routes home — the standard "click the logo" pattern. */}
            <Link
              to="/home"
              onClick={onNavigate}
              aria-label="Proactive Communications Agent — home"
              className="min-w-0 rounded-sm hover:opacity-80 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              <JJWordmark size="sm" />
              <p className="text-muted mt-1 text-[11px] leading-snug">
                J&amp;J Vision · customer communications
              </p>
            </Link>
            {onNavigate ? (
              <button
                onClick={onNavigate}
                className="text-muted hover:text-foreground shrink-0"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            ) : (
              onToggleCollapsed && (
                <button
                  onClick={onToggleCollapsed}
                  aria-label="Collapse sidebar"
                  className="text-muted hover:bg-accent-light hover:text-foreground inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className={cn("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}>
        {NAV.map((section, i) => {
          if (section.adminOnly && !isAdmin) {
            return null;
          }
          return (
            <div key={section.label}>
              {collapsed ? (
                i > 0 && <div className="my-3 border-t border-white/15" aria-hidden />
              ) : (
                <p
                  className={cn(
                    "text-muted px-2 pb-2 text-[10px] font-medium tracking-[0.18em] uppercase",
                    i > 0 && "pt-6",
                  )}
                >
                  {section.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <NavRow
                    key={item.href}
                    item={item}
                    pathname={pathname}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      {collapsed ? (
        <div className="space-y-1 border-t border-white/15 px-2 py-3">
          <Link
            to="/profile"
            onClick={onNavigate}
            aria-label="Profile"
            className="text-primary-foreground flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
          >
            <User className="h-4 w-4" />
          </Link>
          <button
            onClick={onLogout}
            aria-label="Sign out"
            className="text-primary-foreground flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="border-border space-y-3 border-t px-3 py-4">
          <PresenterVotingDialog />
          <Link
            to="/profile"
            onClick={onNavigate}
            className="text-foreground hover:bg-accent-light flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors"
          >
            <User className="h-4 w-4 shrink-0" />
            <div className="min-w-0">
              <p className="truncate font-medium">{displayName || me?.email}</p>
              {displayName && <p className="text-muted truncate text-xs">{me?.email}</p>}
            </div>
          </Link>
          <button
            onClick={onLogout}
            className="text-foreground hover:bg-accent-light flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span>Sign out</span>
          </button>
          <DemoLegend className="border-border border-t px-3 pt-3" />
          {/* Persistent demo disclaimer — every surface reads as fictive data,
              never a real J&J product. */}
          <p className="text-muted-foreground px-3 text-[10px] leading-snug">
            Fictive data for the sake of demonstrating functionality.
          </p>
        </div>
      )}
    </>
  );
}

function NavRow({
  item,
  pathname,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const isActive = pathname === item.href || pathname.startsWith(item.href + "/");

  const baseRow = "group flex items-center rounded-md transition-colors";
  const sizing = collapsed ? "h-10 w-full justify-center" : "gap-2.5 px-3 py-2 text-sm";
  // Collapsed rail sits on J&J red; expanded sits on the card surface.
  const tone = collapsed
    ? isActive
      ? "bg-white text-primary"
      : "text-primary-foreground hover:bg-white/10"
    : isActive
      ? "bg-primary text-primary-foreground"
      : "text-foreground hover:bg-accent-light";

  return (
    <li>
      <Link
        to={item.href}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
        aria-label={collapsed ? item.label : undefined}
        className={cn(baseRow, sizing, tone)}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </Link>
    </li>
  );
}

/** Page title for the top bar, matched most-specific-first. */
function currentTitle(pathname: string): string {
  if (pathname.startsWith("/ordering/")) {
    return "Order detail";
  }
  if (pathname.startsWith("/ordering")) {
    return "Ordering comms";
  }
  if (pathname.startsWith("/share/")) {
    return "Account detail";
  }
  if (pathname.startsWith("/share")) {
    return "Order-share comms";
  }
  if (pathname.startsWith("/guide")) {
    return "Guide";
  }
  if (pathname.startsWith("/profile")) {
    return "Profile";
  }
  if (pathname.startsWith("/admin/users")) {
    return "User management";
  }
  if (pathname.startsWith("/admin/activity")) {
    return "Activity";
  }
  return "Overview";
}
